import { useMemo } from "react";
import type { Partner, PartnerCategory } from "../api/dpp.js";
import { useT } from "../i18n.js";

/**
 * Circularity partner categories. The five canonical keys ship with localized
 * labels; anything else is an org-defined category the user typed in the
 * partner form and renders verbatim — here and on the public passport page.
 */
export const PARTNER_CATEGORIES: PartnerCategory[] = ["donation", "repair", "upcycle", "recycle", "resale"];

const CANONICAL = new Set<string>(PARTNER_CATEGORIES);

export function isCanonicalCategory(category: string): category is PartnerCategory {
  return CANONICAL.has(category);
}

/** Localized label for a canonical key; the raw value for org-defined ones. */
export function usePartnerCategoryLabel(): (category: string) => string {
  const t = useT();
  return useMemo(
    () => (category: string) => (isCanonicalCategory(category) ? t(`partnerCat.${category}`) : category),
    [t],
  );
}

/** The org's own categories, derived from the partners it has already saved. */
export function customCategoriesOf(partners: Partner[]): string[] {
  return [...new Set(partners.map((p) => p.category))]
    .filter((c) => !isCanonicalCategory(c))
    .sort((a, b) => a.localeCompare(b));
}
